import setDynamicFormFieldValue from "@iewccouk/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@iewccouk/Store/Reducers";
import { colorStrings } from "@iewccouk/Utilities/variables";
import { DynamicFormContext } from "@iewccouk/Widgets/DynamicForm/DynamicForm";
import DynamicFormFieldShell from "@iewccouk/Widgets/DynamicForm/DynamicFormFieldShell";
import { useRegisterDynamicFormFieldKeys } from "@iewccouk/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import siteMessage from "@insite/client-framework/SiteMessage";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox from "@insite/mobius/Checkbox";
import CheckboxGroup from "@insite/mobius/CheckboxGroup";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    fieldKey = "fieldKey",
    label = "label",
    isRequired = "isRequired",
    defaultChecked = "defaultChecked",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.fieldKey]: string;
        [fields.label]: string;
        [fields.isRequired]: boolean;
        [fields.defaultChecked]: boolean;
    };
}

/** Maps Dynamic Form Redux state into widget props. */
const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const fieldKey = ownProps.fields.fieldKey || "Marketing_Materials_Opt_In__c";
    return {
        fieldKey,
        fieldValue: state.customDynamicForm?.fieldValues[fieldKey],
    };
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const widgetGroup = "SalesForce Dynamic" as any;

// IEWC styleguide colors checked checkbox labels with primary.main (brand red).
// Keep form field labels at normal text color so checked does not look like an error.
const checkboxStyles = {
    mergeCss: true,
    css: css`
        ${IconWrapper} {
            &[role="checkbox"] {
                &[aria-checked="true"],
                &[aria-checked="mixed"] {
                    & + label {
                        color: ${getColor(colorStrings.textMain)};
                        font-weight: 400;
                    }
                }
            }
        }
    `,
};

/** Salesforce Dynamic Form checkbox field widget. */
const DynamicCheckbox: React.FC<Props> = ({
    id,
    fields: widgetFields,
    fieldKey,
    fieldValue,
    setDynamicFormFieldValue,
}) => {
    const { label, isRequired, defaultChecked } = widgetFields;
    const formContext = React.useContext(DynamicFormContext);
    const [errorMessage, setErrorMessage] = React.useState("");
    useRegisterDynamicFormFieldKeys(id, [fieldKey]);

    React.useEffect(() => {
        if (typeof fieldValue === "undefined") {
            setDynamicFormFieldValue({
                key: fieldKey,
                value: defaultChecked ? "true" : "false",
            });
        }
    }, [fieldKey, fieldValue, defaultChecked, setDynamicFormFieldValue]);

    const isChecked = fieldValue === "true";

    const validate = React.useCallback(() => {
        const error = isRequired && !isChecked ? (siteMessage("Field_Required", label) as string) : "";
        setErrorMessage(error);
        return !error;
    }, [isChecked, isRequired, label]);

    // Register for submit validation only — do not validate on mount.
    React.useEffect(() => {
        formContext.validators[fieldKey] = validate;
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, validate, formContext.validators]);

    // After submit showed an error, clear it as soon as the box becomes valid.
    React.useEffect(() => {
        if (errorMessage && isChecked) {
            setErrorMessage("");
        }
    }, [isChecked, errorMessage]);

    return (
        <DynamicFormFieldShell>
            <CheckboxGroup error={errorMessage || undefined}>
                <Checkbox
                    {...checkboxStyles}
                    checked={isChecked}
                    onChange={(_, checked) =>
                        setDynamicFormFieldValue({ key: fieldKey, value: checked ? "true" : "false" })
                    }
                >
                    {label}
                    {isRequired ? " *" : ""}
                </Checkbox>
            </CheckboxGroup>
        </DynamicFormFieldShell>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(DynamicCheckbox),
    definition: {
        group: widgetGroup,
        displayName: "Checkbox",
        icon: "square-check",
        fieldDefinitions: [
            {
                name: fields.fieldKey,
                displayName: "Salesforce Field (API Name)",
                editorTemplate: "TextField",
                fieldType: "General",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
            },
            {
                name: fields.defaultChecked,
                displayName: "Default Checked",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
